import { t, type I18nLocale } from '@ardoq/i18n';
import type {
  ActionsCellLabels,
  FloatingActionBarLabels,
  GroupCellLabels,
  NameCellLabels,
  SelectionColumnLabels,
  TableEmptyStateLabels,
  TableLocaleTextLabels,
  TransformationCellLabels,
} from '@ardoq/table';

export const getNameCellLabels = (i18nLocale: I18nLocale): NameCellLabels => ({
  copyName: t(i18nLocale, 'table:nameCell.copyName', 'Copy name'),
});

export const getTransformationCellLabels = (
  i18nLocale: I18nLocale
): TransformationCellLabels => ({
  triggerRecalculation: t(
    i18nLocale,
    'table:transformationCell.triggerRecalculation',
    'Trigger recalculation'
  ),
});

export const getActionsCellLabels = (
  i18nLocale: I18nLocale
): ActionsCellLabels => ({
  actions: t(i18nLocale, 'table:actionsCell.actions', 'Actions'),
});

export const getGroupCellLabels = (
  i18nLocale: I18nLocale
): GroupCellLabels => ({
  dragHandle: t(i18nLocale, 'table:groupCell.dragHandle', 'Drag to reorder'),
  collapseGroup: t(
    i18nLocale,
    'table:groupCell.collapseGroup',
    'Collapse group'
  ),
  expandGroup: t(i18nLocale, 'table:groupCell.expandGroup', 'Expand group'),
  inlineTextEditor: t(
    i18nLocale,
    'table:groupCell.inlineTextEditor',
    'Inline text editor'
  ),
});

export const getTableEmptyStateLabels = (
  i18nLocale: I18nLocale
): TableEmptyStateLabels => ({
  noResultsTitle: t(
    i18nLocale,
    'table:emptyState.noResultsTitle',
    'No results found'
  ),
  noResultsDescription: t(
    i18nLocale,
    'table:emptyState.noResultsDescription',
    'You may have applied filters, or no data has been added yet.'
  ),
});

export const getTableLocaleTextLabels = (
  i18nLocale: I18nLocale
): TableLocaleTextLabels => ({
  sortAscending: t(
    i18nLocale,
    'table:localeText.sortAscending',
    'Sort Ascending'
  ),
  sortDescending: t(
    i18nLocale,
    'table:localeText.sortDescending',
    'Sort Descending'
  ),
  sortUnSort: t(i18nLocale, 'table:localeText.sortUnSort', 'Clear Sort'),
  pinColumn: t(i18nLocale, 'table:localeText.pinColumn', 'Pin Column'),
  pinLeft: t(i18nLocale, 'table:localeText.pinLeft', 'Pin Left'),
  pinRight: t(i18nLocale, 'table:localeText.pinRight', 'Pin Right'),
  noPin: t(i18nLocale, 'table:localeText.noPin', 'No Pin'),
  columnChooser: t(
    i18nLocale,
    'table:localeText.columnChooser',
    'Choose Columns'
  ),
  chooseColumns: t(
    i18nLocale,
    'table:localeText.chooseColumns',
    'Choose Columns'
  ),
  resetColumns: t(i18nLocale, 'table:localeText.resetColumns', 'Reset Columns'),
  expandAll: t(
    i18nLocale,
    'table:localeText.expandAll',
    'Expand All Row Groups'
  ),
  contractAll: t(
    i18nLocale,
    'table:localeText.contractAll',
    'Close All Row Groups'
  ),
  autosizeThisColumn: t(
    i18nLocale,
    'table:localeText.autosizeThisColumn',
    'Autosize This Column'
  ),
  autosizeAllColumns: t(
    i18nLocale,
    'table:localeText.autosizeAllColumns',
    'Autosize All Columns'
  ),
  rowGroupColumnsEmptyMessage: t(
    i18nLocale,
    'table:localeText.rowGroupColumnsEmptyMessage',
    'Drag columns here to set row groups'
  ),
  noRowsToShow: t(
    i18nLocale,
    'table:localeText.noRowsToShow',
    'No Rows To Show'
  ),
  loadingOoo: t(i18nLocale, 'table:localeText.loadingOoo', 'Loading…'),
  ariaRowToggleSelection: t(
    i18nLocale,
    'table:localeText.ariaRowToggleSelection',
    'Select row'
  ),
  ariaRowSelectionDisabled: t(
    i18nLocale,
    'table:localeText.ariaRowSelectionDisabled',
    'Selection is not available for this row'
  ),
  ariaHeaderSelection: t(
    i18nLocale,
    'table:localeText.ariaHeaderSelection',
    'Select all rows'
  ),
  ariaRowSelectAll: t(
    i18nLocale,
    'table:localeText.ariaRowSelectAll',
    'Toggle selection of all rows'
  ),
});

export const getSelectionColumnLabels = (
  i18nLocale: I18nLocale
): SelectionColumnLabels => ({
  header: t(i18nLocale, 'table:selectionColumn.header', 'Select'),
});

const buildFormatSelectedCount =
  (i18nLocale: I18nLocale) =>
  ({
    selectedCount,
    entityName,
  }: {
    selectedCount: number;
    entityName: string;
  }): string =>
    t(
      i18nLocale,
      'table:floatingActionBar.selectedCount',
      { selectedCount, entityName },
      '{{selectedCount}} {{entityName}} selected'
    );

export const getFloatingActionBarLabels = (
  i18nLocale: I18nLocale
): FloatingActionBarLabels => ({
  actionsForSelectedItems: t(
    i18nLocale,
    'table:floatingActionBar.actionsForSelectedItems',
    'Actions for selected items'
  ),
  moreActions: t(
    i18nLocale,
    'table:floatingActionBar.moreActions',
    'More actions'
  ),
  cancel: t(i18nLocale, 'table:floatingActionBar.cancel', 'Cancel'),
  formatSelectedCount: buildFormatSelectedCount(i18nLocale),
});

export type TableLabels = {
  nameCell: NameCellLabels;
  transformationCell: TransformationCellLabels;
  actionsCell: ActionsCellLabels;
  groupCell: GroupCellLabels;
  emptyState: TableEmptyStateLabels;
  localeText: TableLocaleTextLabels;
  selectionColumn: SelectionColumnLabels;
  floatingActionBar: FloatingActionBarLabels;
};

export const getTableLabels = (i18nLocale: I18nLocale): TableLabels => ({
  nameCell: getNameCellLabels(i18nLocale),
  transformationCell: getTransformationCellLabels(i18nLocale),
  actionsCell: getActionsCellLabels(i18nLocale),
  groupCell: getGroupCellLabels(i18nLocale),
  emptyState: getTableEmptyStateLabels(i18nLocale),
  localeText: getTableLocaleTextLabels(i18nLocale),
  selectionColumn: getSelectionColumnLabels(i18nLocale),
  floatingActionBar: getFloatingActionBarLabels(i18nLocale),
});
